import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
// Importing this closes a cycle (ApiSlice -> BackendApiClient -> HelperMethods ->
// ReduxHooks -> here). It is safe only because nothing in that chain reads `store` or
// `ApiSlice` at module-evaluation time — keep it that way, or move the BackendApiClient
// import inside backendBaseQuery as a dynamic import().
import ApiSlice from '../services/api/ApiSlice';
import EventHelperSlice from './slices/EventHelperSlice';
import SideBarSlice from './slices/SideBarSlice';
import CompanySlice from './slices/CompanySlice';
import PrintSlice from './slices/PrintSlice';
import { setupListeners } from '@reduxjs/toolkit/query';

const persistConfig = {
  key: 'root',
  storage,
  // Server state, not user state — RTK Query refetches it, so persisting the cache would
  // only ever hand the app a stale list to render before the first response lands.
  blacklist: [ApiSlice.reducerPath, PrintSlice.reducerPath],
};

export const reducersCombined = combineReducers({
  sidebar: SideBarSlice.reducer,
  eventHelper: EventHelperSlice.reducer,
  company: CompanySlice.reducer,
  print: PrintSlice.reducer,
  [ApiSlice.reducerPath]: ApiSlice.reducer,
});

const rootReducer = (state, actions) => {
  if (actions.type === 'RESET_STORE') {
    state = undefined;
  }

  return reducersCombined(state, actions);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // redux-persist puts non-serializable callbacks on its own lifecycle actions.
      serializableCheck: {
        ignoredActions: [FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE],
      },
    }).concat(ApiSlice.middleware),
});

// Only subscriptions that opt in react to this — usePrinters passes refetchOnReconnect.
// refetchOnFocus is deliberately left off everywhere.
setupListeners(store.dispatch);

export const persistor = persistStore(store);
